import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { X, Car, FileText, Check, Calendar, UserMinus, Copy, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { getManualStopReasonLabel } from '@/lib/validation/manualStopValidation';

export type HighlightState = 'none' | 'blue' | 'red';

export interface RecallCardData {
  vin: string;
  campaign_number: string;
  recall_title?: string;
  year?: string | number;
  make?: string;
  model?: string;
  dealer_name?: string;
  dealer_address?: string;
  dealer_city?: string;
  dealer_region?: string;
  // Customer stops (ids only; the list shows one contact reveal per household)
  stop_kind?: 'business' | 'customer';
  contact_id?: string | null;
  // Assignment info (if assigned)
  assignedTo?: string;
  assignmentId?: string;
  scheduledDate?: string; // Date only (no time)
  assignedByName?: string; // Name of user who assigned the recall
  // Manual stop support
  is_manual_stop?: boolean;
  manual_stop_reason?: string;
  manual_stop_notes?: string;
  /** Note from the dispatcher (customer and business manual stops) */
  dispatcher_note?: string | null;
}

interface RecallAssignmentCardProps {
  recall: RecallCardData;
  isSelected: boolean;
  isAssigned: boolean;
  onSelect: (vin: string, campaignNumber: string) => void;
  onUnassign?: (assignmentId: string) => void;
  onRemove?: (vin: string, campaignNumber: string) => void;
  disabled?: boolean;
  className?: string;
  allowReassignment?: boolean;
  highlightState?: HighlightState;
}

/**
 * Compact recall card with checkbox for assignment selection
 * Shows vehicle info, VIN, and campaign number
 */
// eslint-disable-next-line complexity
export const RecallAssignmentCard: React.FC<RecallAssignmentCardProps> = ({
  recall,
  isSelected,
  isAssigned,
  onSelect,
  onUnassign,
  onRemove,
  disabled = false,
  className = '',
  allowReassignment = false,
  highlightState = 'none'
}) => {
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger if clicking on the unassign button
    if ((e.target as HTMLElement).closest('.unassign-btn')) {
      return;
    }
    // Allow selection for reassignment if enabled
    if (!disabled && (!isAssigned || allowReassignment)) {
      onSelect(recall.vin, recall.campaign_number);
    }
  };

  const handleCheckboxChange = () => {
    // Allow selection for reassignment if enabled
    if (!disabled && (!isAssigned || allowReassignment)) {
      onSelect(recall.vin, recall.campaign_number);
    }
  };

  const handleUnassign = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (recall.assignmentId && onUnassign) {
      onUnassign(recall.assignmentId);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(recall.vin, recall.campaign_number);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(recall.vin, recall.campaign_number);
    }
  };

  // Format VIN for display (show full VIN)
  const formatVin = (vin: string): string => {
    return vin;
  };

  const { toast } = useToast();
  const [vinCopied, setVinCopied] = React.useState(false);

  const handleCopyVin = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!recall.vin) return;

    try {
      await navigator.clipboard.writeText(recall.vin);
      setVinCopied(true);
      toast({
        title: 'Success',
        description: 'VIN copied to clipboard!',
        variant: 'success'
      });
      setTimeout(() => setVinCopied(false), 2000);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to copy VIN to clipboard',
        variant: 'destructive'
      });
    }
  };

  // Build vehicle description - for manual stops without vehicle info, show reason
  const vehicleDescription =
    recall.is_manual_stop && !recall.year && !recall.make && !recall.model
      ? recall.manual_stop_reason || 'Manual Stop'
      : [recall.year, recall.make, recall.model].filter(Boolean).join(' ');

  const isInteractive = !disabled && (!isAssigned || allowReassignment);

  const renderActionButton = () => {
    if (isAssigned && onUnassign && recall.assignmentId) {
      return (
        <button
          onClick={handleUnassign}
          className={cn(
            'unassign-btn flex items-center justify-center h-7 w-7 rounded-full',
            'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300',
            'hover:bg-orange-200 dark:hover:bg-orange-900/70 transition-colors',
            'border border-orange-300 dark:border-orange-700'
          )}
          title='Unassign from technician'
        >
          <UserMinus className='h-3.5 w-3.5' />
        </button>
      );
    }
    if (!isAssigned && onRemove) {
      return (
        <button
          onClick={handleRemove}
          className={cn(
            'unassign-btn flex items-center justify-center h-7 w-7 rounded-full',
            'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
            'hover:bg-red-200 dark:hover:bg-red-900/70 transition-colors',
            'border border-red-300 dark:border-red-700'
          )}
          title='Remove from assignment list'
        >
          <X className='h-3.5 w-3.5' />
        </button>
      );
    }
    return null;
  };

  return (
    <div
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={handleCardClick}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      className={cn(
        'group relative flex flex-row gap-3 p-3 rounded-lg border transition-all duration-200 items-start',
        'bg-card hover:bg-muted/30',
        // Manual stop base styling - accent-tinted background + left border stripe
        recall.is_manual_stop &&
          !isAssigned &&
          highlightState === 'none' &&
          'border-l-4 border-l-accent',
        recall.is_manual_stop &&
          !isAssigned &&
          highlightState === 'none' &&
          !isSelected &&
          'bg-accent/5 border-accent/30 dark:bg-accent/10 dark:border-accent/40',
        recall.is_manual_stop &&
          !isAssigned &&
          highlightState === 'none' &&
          isSelected &&
          'bg-accent/10 border-accent/40 dark:bg-accent/15',
        // Regular recall selected state (unassigned)
        !recall.is_manual_stop &&
          isSelected &&
          !isAssigned &&
          'bg-primary/5 border-primary/30 dark:bg-primary/10',
        // Blue highlight - assigned for selected date
        highlightState === 'blue' &&
          !isSelected &&
          'ring-2 ring-blue-500 bg-blue-500/10 dark:bg-blue-500/20 border-blue-500',
        highlightState === 'blue' &&
          isSelected &&
          'ring-2 ring-blue-600 bg-blue-500/15 dark:bg-blue-500/25 border-blue-600',
        // Red highlight - overdue (assigned for a past date)
        highlightState === 'red' &&
          !isSelected &&
          'ring-2 ring-red-500 bg-red-500/10 dark:bg-red-500/20 border-red-500',
        highlightState === 'red' &&
          isSelected &&
          'ring-2 ring-red-600 bg-red-500/15 dark:bg-red-500/25 border-red-600',
        // Assigned but not highlighted
        isAssigned && highlightState === 'none' && 'bg-muted/20 border-border/60',
        isInteractive && 'cursor-pointer',
        disabled && 'opacity-50 cursor-not-allowed',
        !recall.is_manual_stop &&
          !isAssigned &&
          highlightState === 'none' &&
          !isSelected &&
          'border-border',
        className
      )}
    >
      {/* Checkbox or Check Icon */}
      <div className='pt-0.5 flex-shrink-0'>
        {isAssigned && !allowReassignment ? (
          <div
            className={cn(
              'w-5 h-5 rounded-full flex items-center justify-center border',
              'bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-100',
              'border-purple-300 dark:border-purple-600'
            )}
          >
            <Check className='h-3.5 w-3.5' />
          </div>
        ) : (
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleCheckboxChange}
            disabled={disabled}
            className={cn(
              'transition-all border-2 w-5 h-5',
              isSelected && 'border-primary bg-primary text-white',
              'data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-white'
            )}
            style={{
              backgroundColor: isSelected ? 'hsl(var(--primary))' : undefined,
              borderColor: isSelected ? 'hsl(var(--primary))' : undefined,
              color: isSelected ? 'white' : undefined
            }}
          />
        )}
      </div>

      {/* Left Column: Vehicle & Recall Info */}
      <div className='flex-1 min-w-0 space-y-1'>
        {/* Vehicle Info */}
        <div className='flex items-center gap-2 min-w-0'>
          <Car className='h-4 w-4 flex-shrink-0 text-muted-foreground' />
          <span className='font-semibold text-sm text-foreground truncate'>
            {vehicleDescription || 'Unknown Vehicle'}
          </span>
        </div>

        {/* VIN and Campaign Stack */}
        <div className='flex flex-col gap-0.5'>
          {/* VIN - WCAG AA compliant: minimum 4.5:1 contrast ratio */}
          <div className='flex items-center gap-1.5 min-w-0'>
            <code
              className={cn(
                'font-mono text-xs px-1.5 py-0.5 rounded border truncate',
                'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600',
                'text-slate-700 dark:text-slate-200'
              )}
            >
              {formatVin(recall.vin)}
            </code>
            <button
              type='button'
              onClick={handleCopyVin}
              title='Copy VIN to clipboard'
              className='inline-flex items-center justify-center h-5 w-5 rounded hover:bg-muted'
            >
              {vinCopied ? (
                <Check className='h-3 w-3 text-green-600 dark:text-green-400' />
              ) : (
                <Copy className='h-3 w-3 text-slate-500 dark:text-slate-400 hover:text-foreground' />
              )}
            </button>
          </div>

          {/* Campaign or Manual Stop Badge - High contrast for readability */}
          {recall.is_manual_stop ? (
            <div className='flex items-center gap-1.5 min-w-0 flex-wrap'>
              <Wrench className='h-3 w-3 flex-shrink-0 text-accent' />
              <Badge
                variant='outline'
                className='text-xs bg-accent/15 text-accent border border-accent/40 dark:bg-accent/20'
              >
                Manual Stop
              </Badge>
              {recall.manual_stop_reason && (
                <span className='text-xs text-slate-600 dark:text-slate-300'>
                  - {getManualStopReasonLabel(recall.manual_stop_reason)}
                </span>
              )}
              {/* Campaign code - if user entered one (not placeholder) */}
              {recall.campaign_number && !recall.campaign_number.startsWith('MANUAL') && (
                <span className='text-xs text-slate-600 dark:text-slate-300'>
                  - {recall.campaign_number}
                </span>
              )}
              {/* Notes - if present */}
              {recall.manual_stop_notes && (
                <span
                  className='text-xs text-slate-500 dark:text-slate-400 truncate min-w-0'
                  title={recall.manual_stop_notes}
                >
                  - {recall.manual_stop_notes}
                </span>
              )}
              {recall.dispatcher_note && (
                <span
                  className='text-xs text-slate-500 dark:text-slate-400 truncate min-w-0'
                  title={recall.dispatcher_note}
                >
                  - Note for technician: {recall.dispatcher_note}
                </span>
              )}
            </div>
          ) : (
            <div className='flex items-center gap-1.5 min-w-0'>
              <FileText className='h-3 w-3 flex-shrink-0 text-blue-600 dark:text-blue-400' />
              <span className='font-mono font-semibold text-xs text-foreground shrink-0'>
                {recall.campaign_number}
              </span>
              {recall.recall_title && (
                <span
                  className='text-xs text-slate-600 dark:text-slate-300 truncate min-w-0'
                  title={recall.recall_title}
                >
                  - {recall.recall_title}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Column: Assignment & Actions */}
      <div className='flex flex-col items-end gap-1.5 flex-shrink-0 ml-2 max-w-[45%]'>
        {/* Actions Row */}
        <div className='flex items-center justify-end gap-2'>
          {!isAssigned && isSelected && (
            <Badge
              variant='outline'
              className='text-xs border-primary/50 text-primary bg-primary/10 dark:bg-primary/20 px-2 py-0.5'
            >
              Selected
            </Badge>
          )}

          {renderActionButton()}
        </div>

        {/* Assignment Details - Compact Badge Style with high contrast */}
        {isAssigned && (
          <div className='flex flex-col items-end gap-1'>
            <Badge
              variant='secondary'
              title={recall.assignedTo || 'Unknown Tech'}
              className={cn(
                'text-xs bg-purple-100 dark:bg-purple-800',
                'text-purple-800 dark:text-purple-100',
                'border border-purple-300 dark:border-purple-600',
                'max-w-full truncate'
              )}
            >
              {recall.assignedTo || 'Unknown Tech'}
            </Badge>
            {recall.scheduledDate && (
              <div className='flex items-center gap-1 text-xs text-slate-700 dark:text-slate-200'>
                <Calendar className='h-3 w-3' />
                <span>{recall.scheduledDate}</span>
              </div>
            )}
            {recall.assignedByName && (
              <span
                className='text-xs text-slate-600 dark:text-slate-300 max-w-full truncate'
                title={`by ${recall.assignedByName}`}
              >
                by {recall.assignedByName}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecallAssignmentCard;
